import React, { useCallback, useEffect, useState } from "react";
import { graphqlRequest } from "../../../../utils/graphqlRequest";

const LIST = `query ContractorClaimsAdmin {
  contractorPayouts { id status abaPayRunId }
  contractorPayoutRequests {
    id reference contractorId businessName abnNumber businessAddress contractorEmail cycle
    periodStartDate periodEndDate sourceKeys lineItems { sourceKey projectId projectName date description hours rate amount }
    subtotal gst total note reviewNote managerReviewNote managerReviewedAt managerId
    status payoutId paidAt paymentReference remittanceEmailedAt createdAt
  }
}`;
const WORK = `query ClaimSources($contractorId: String, $startDate: String, $endDate: String) {
  contractorWorkEntries(contractorId: $contractorId, startDate: $startDate, endDate: $endDate) {
    id source date status workDescription hours rate amount project { id name }
  }
}`;
const REVIEW = `mutation ReviewClaim($id: String!, $approve: Boolean!, $note: String, $gstConfirmed: Boolean) {
  reviewContractorPayoutRequest(id: $id, approve: $approve, note: $note, gstConfirmed: $gstConfirmed) { id status payoutId }
}`;
const PAY = `mutation ConfirmClaimPayment($id: String!, $paymentReference: String!) {
  confirmContractorPayoutPayment(id: $id, paymentReference: $paymentReference) { id status remittanceEmailedAt }
}`;
const ABA = `mutation GenerateClaimAba($payoutIds: [String!]!) {
  generateContractorAba(payoutIds: $payoutIds) { id status employeeCount }
}`;
const RESEND = `mutation ResendClaimRemittance($id: String!) {
  resendContractorRemittance(id: $id) { id remittanceEmailedAt }
}`;
const money = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(n) || 0);

export default function ContractorClaimsPanel() {
  const [claims, setClaims] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sources, setSources] = useState([]);
  const [note, setNote] = useState("");
  const [gstConfirmed, setGstConfirmed] = useState(false);
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const reload = useCallback(async () => {
    try {
      const result = await graphqlRequest(LIST);
      setClaims(result?.contractorPayoutRequests || []);
      setPayouts(result?.contractorPayouts || []);
      setError("");
    }
    catch (e) { setError(e?.message || "Could not load contractor claims"); }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (!selected) return;
    let active = true;
    graphqlRequest(WORK, { contractorId: selected.contractorId, startDate: selected.periodStartDate, endDate: selected.periodEndDate })
      .then((data) => { if (active) setSources(data?.contractorWorkEntries || []); })
      .catch((e) => { if (active) setError(e?.message || "Could not load work details"); });
    return () => { active = false; };
  }, [selected]);
  const action = async (query, variables) => {
    setBusy(true); setError("");
    try { await graphqlRequest(query, variables); await reload(); setSelected(null); setNote(""); setReference(""); }
    catch (e) { setError(e?.message || "Action failed"); }
    finally { setBusy(false); }
  };
  const linkedPayout = selected ? payouts.find((row) => row.id === selected.payoutId) : null;
  const chosen = selected ? sources.filter((row) =>
    selected.sourceKeys.includes(`${row.source === "manual" ? "work-entry" : "timecard"}:${row.id}`)) : [];
  const reconciled = selected && chosen.length === selected.sourceKeys.length &&
    chosen.every((row) => {
      const line = selected.lineItems?.find((item) => item.sourceKey === `${row.source === "manual" ? "work-entry" : "timecard"}:${row.id}`);
      return String(row.status).toLowerCase() === "approved" && row.project?.id && line &&
        row.project.id === line.projectId && row.date === line.date &&
        Number(row.hours) === Number(line.hours) && Number(row.rate) === Number(line.rate) &&
        Number(row.amount) === Number(line.amount);
    }) &&
    Math.round(chosen.reduce((sum, row) => sum + Number(row.amount), 0) * 100) === Math.round(Number(selected.subtotal) * 100);
  return <section style={{ border: "1px solid #cbd5e1", borderRadius: 12, padding: 20, marginBottom: 24 }}>
    <h2>Contractor payment claims</h2>
    <p>Verify manager-approved claims, business details and GST registration before creating a payout.</p>
    <button type="button" onClick={reload}>Refresh claims</button>
    {error && <p role="alert" style={{ color: "#b91c1c" }}>{error}</p>}
    {claims.length === 0 ? <p>No contractor claims received.</p> : <div style={{ overflowX: "auto" }}>
      <table className="payout-table" style={{ width: "100%" }}><thead><tr>
        <th>Contractor business</th><th>Claim</th><th>Period</th><th>Amount</th><th>Status</th><th>Action</th>
      </tr></thead><tbody>{claims.map((claim) => <tr key={claim.id}>
        <td>{claim.businessName}<br />ABN {claim.abnNumber || "Not supplied"}</td>
        <td>{claim.reference}</td><td>{claim.periodStartDate} – {claim.periodEndDate}</td>
        <td>{money(claim.total)}</td><td>{claim.status}</td>
        <td><button type="button" onClick={() => { setSelected(claim); setSources([]); setNote(""); setGstConfirmed(false); }}>
          {claim.status === "manager_approved" ? "Verify" : "View"}
        </button></td>
      </tr>)}</tbody></table>
    </div>}
    {selected && <div style={{ marginTop: 20, padding: 18, border: "1px solid #94a3b8", borderRadius: 10 }}>
      <h3>{selected.businessName} · {selected.reference}</h3>
      <p>ABN: {selected.abnNumber || "Missing"} · Address: {selected.businessAddress || "Not supplied"} · Email: {selected.contractorEmail}</p>
      <p>Cycle: {selected.cycle} · {selected.periodStartDate} to {selected.periodEndDate}</p>
      <p>{selected.note || "No contractor message"}</p><p>Manager approval: {selected.managerReviewedAt || "Not recorded"} · {selected.managerReviewNote || "No manager note"}</p>
      <table style={{ width: "100%" }}><thead><tr><th>Project</th><th>Date</th><th>Work</th><th>Hours</th><th>Rate</th><th>Amount</th></tr></thead>
        <tbody>{chosen.map((row) => <tr key={row.id}>
          <td>{row.project?.name || "Timesheet"}</td><td>{row.date}</td><td>{row.workDescription}</td>
          <td>{row.hours}</td><td>{money(row.rate)}</td><td>{money(row.amount)}</td>
        </tr>)}</tbody></table>
      <p>Work reconciliation: <strong>{reconciled ? "Matched" : "Missing or changed work — cannot approve"}</strong></p>
      <p>Submitted line items: {(selected.lineItems || []).map((line) => `${line.projectName} ${line.date} ${money(line.amount)}`).join(" · ")}</p>
      <p>Subtotal {money(selected.subtotal)} · Claimed GST {money(selected.gst)} · Total {money(selected.total)}</p>
      {selected.status === "manager_approved" && <>
        {Number(selected.gst) > 0 && <label style={{ display: "block" }}>
          <input type="checkbox" checked={gstConfirmed} onChange={(e) => setGstConfirmed(e.target.checked)} />
          {' '}I checked the contractor ABN and GST registration for this claim.
        </label>}
        <label>Review note <input value={note} onChange={(e) => setNote(e.target.value)} /></label>{' '}
        <button disabled={busy || !reconciled || (Number(selected.gst) > 0 && (!gstConfirmed || !selected.abnNumber))} onClick={() => action(REVIEW, { id: selected.id, approve: true, note, gstConfirmed })}>Verify and create payout</button>{' '}
        <button disabled={busy || !note.trim()} onClick={() => action(REVIEW, { id: selected.id, approve: false, note })}>Return for correction</button>
      </>}
      {selected.status === "approved" && <p>
        {linkedPayout?.status === "pending" && <button disabled={busy} onClick={() => action(ABA, { payoutIds: [selected.payoutId] })}>
          Generate bank ABA file
        </button>}
        {linkedPayout?.status === "aba_queued" && <span>ABA queued · approve and release in ABA Pay Runs before confirming settlement. </span>}
        <label>Bank or cash payment reference <input required value={reference} onChange={(e) => setReference(e.target.value)} /></label>{' '}
        <button disabled={busy || !reference.trim() || !selected.managerReviewedAt} onClick={() => action(PAY, { id: selected.id, paymentReference: reference })}>
          Confirm settlement and send remittance
        </button>
      </p>}
      {selected.status === "paid" && <p>Paid {selected.paidAt} · Reference {selected.paymentReference}
        {' '}<button disabled={busy} onClick={() => action(RESEND, { id: selected.id })}>Resend remittance email</button>
        {selected.remittanceEmailedAt ? " · Email sent" : " · Email pending"}
      </p>}
      {selected.status === "rejected" && <p>Reason: {selected.reviewNote}</p>}
      <button type="button" onClick={() => setSelected(null)}>Close</button>
    </div>}
  </section>;
}
